import React from "react"
import { TextField, IconButton } from "@mui/material"
import { GripVertical, Trash2 } from "lucide-mui"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import i18n from "@/i18n"
import type { Section } from "./types"

const STYLES = {
  sectionContainer: "relative bg-gray-50 rounded-lg p-4 mb-3 border border-gray-300",
  dragHandle: "cursor-grab text-gray-400 hover:text-gray-600 bg-transparent border-0 p-1",
  deleteButton: "text-gray-400 hover:text-red-500",
  titleField: "mb-3",
  actionsRow: "flex justify-end mt-2",
  icon: "w-5 h-5",
} as const

export type SectionEditorProps = {
  section: Section
  index: number
  onUpdate: (index: number, field: "title" | "content", value: string) => void
  onDelete: (index: number) => void
  customSectionsEnabled: boolean
}

export const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  index,
  onUpdate,
  onDelete,
  customSectionsEnabled,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id || section.tempId || `section-${index}`,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={STYLES.sectionContainer}
      data-testid={`settings-venue-rules-sections-item-${index}`}
    >
      <TextField
        value={section.title}
        onChange={(e) => onUpdate(index, "title", e.target.value)}
        placeholder={i18n.t("app.settings.venues.reservation_rules.sections.section_title_placeholder")}
        size="small"
        fullWidth
        variant="outlined"
        className={STYLES.titleField}
        data-testid={`settings-venue-rules-sections-item-${index}-input-title`}
      />
      <TextField
        value={section.content}
        onChange={(e) => onUpdate(index, "content", e.target.value)}
        multiline
        minRows={3}
        fullWidth
        placeholder={i18n.t("app.settings.venues.reservation_rules.sections.section_content_placeholder")}
        variant="outlined"
        size="small"
        data-testid={`settings-venue-rules-sections-item-${index}-input-content`}
      />
      <div className={STYLES.actionsRow}>
        {customSectionsEnabled && (
          <IconButton
            onClick={() => onDelete(index)}
            className={STYLES.deleteButton}
            size="small"
            data-testid={`settings-venue-rules-sections-item-${index}-btn-delete`}
          >
            <Trash2 className={STYLES.icon} />
          </IconButton>
        )}
        <IconButton
          {...attributes}
          {...listeners}
          className={STYLES.dragHandle}
          data-testid={`settings-venue-rules-sections-item-${index}-btn-drag`}
        >
          <GripVertical className={STYLES.icon} />
        </IconButton>
      </div>
    </div>
  )
}
